import { Box, Button, Center, Column, Modal, OTPInput, Typography } from '@tempmee/react';
import { OutlineXIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { useEffect, useState } from 'react';
import { mapOTPError, useSendMfaCode, useVerifyMfaCode } from '~/domain/PhoneVerification';
import { formatPhoneForDisplay } from '~/utils/formatPhoneOnChange';

const CODE_LENGTH = 5;
const RESEND_TIMER_SECONDS = 180;

export interface IMfaModalProps {
  open: boolean;
  /** E.164 phone (e.g. +14155551234) — sent to the BE; displayed pretty-formatted. */
  phone: string;
  onClose: () => void;
  onVerified: () => void;
}

const formatForDisplay = (phone: string): string => formatPhoneForDisplay(phone);

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const MfaModal = ({ open, phone, onClose, onVerified }: IMfaModalProps) => {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const { mutate: sendCode } = useSendMfaCode();
  const { mutateAsync: verifyCode, isPending: isVerifying } = useVerifyMfaCode();

  useEffect(() => {
    if (!open) {
      setCode('');
      setErrorMessage(null);
      setTimeRemaining(0);
      return;
    }
    sendCode({ phone });
    setTimeRemaining(RESEND_TIMER_SECONDS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);

  useEffect(() => {
    if (timeRemaining <= 0) return undefined;
    const id = setInterval(() => {
      setTimeRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [timeRemaining]);

  const handleResend = () => {
    if (timeRemaining > 0) return;
    sendCode({ phone });
    setErrorMessage(null);
    setTimeRemaining(RESEND_TIMER_SECONDS);
  };

  const submit = async (otpCode: string) => {
    setErrorMessage(null);
    try {
      await verifyCode({ phone, otpCode });
      onVerified();
    } catch (error) {
      // Run the BE message through the shared OTP error map so users see the
      // friendly copy ("Incorrect code. Please try again." etc.) instead of
      // the raw BE string.
      const beMessage = error instanceof Error ? error.message : '';
      setErrorMessage(mapOTPError(beMessage).message);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Closing clears the typed code and reopening texts a new one, so a stray
      // click outside must not close it. The X still does.
      closeOnOverlayClick={false}
      size="lg"
      style={{ width: 500, maxWidth: 500 }}
    >
      <Box style={{ position: 'relative', paddingBottom: 8 }}>
        <Box
          as="span"
          role="button"
          aria-label="Close"
          tabIndex={0}
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            cursor: 'pointer',
            display: 'inline-flex',
          }}
        >
          <OutlineXIcon size={24} color={colors.secondary[500]} />
        </Box>

        <Column align="center" px={8} py={8}>
          <Typography variant="subtitle1"
            as="h2"
           
            color={colors.neutral[600]}
            textAlign="center"
            style={{ margin: 0 }}
          >
            Confirmation Code
          </Typography>
          <Typography
            variant="body1"
            color={colors.neutral[500]}
            textAlign="center"
            style={{ lineHeight: 1.4, marginTop: 16, marginBottom: 8 }}
          >
            We sent you a 5 digit confirmation code to
            <br />
            <Typography as="span" bold color={colors.neutral[500]}>
              {formatForDisplay(phone)}
            </Typography>
            .
          </Typography>
          <Center>
            <OTPInput
              length={CODE_LENGTH}
              value={code}
              onChange={setCode}
              onComplete={submit}
              error={Boolean(errorMessage)}
              autoFocus
            />
          </Center>
          {errorMessage && (
            <Typography
              variant="body1"
              color={colors.error[500]}
              textAlign="center"
              role="alert"
              style={{ marginTop: 8 }}
            >
              {errorMessage}
            </Typography>
          )}
          {timeRemaining > 0 ? (
            <Typography
              variant="body1"
              color={colors.neutral[500]}
              textAlign="center"
              style={{ marginTop: 16, marginBottom: 24 }}
            >
              Resend code in{' '}
              <Typography as="span" bold color={colors.primary[500]}>
                {formatTime(timeRemaining)}
              </Typography>
            </Typography>
          ) : (
            <Button
              variant="ghost"
              size="md"
              onClick={handleResend}
              style={{ textDecoration: 'none', marginTop: 8, marginBottom: 16 }}
            >
              Resend code
            </Button>
          )}
          <Box>
            <Button
              type="button"
              variant="primary"
              size="lg"
              isLoading={isVerifying}
              disabled={code.length < CODE_LENGTH}
              onClick={() => submit(code)}
              style={{ width: 152 }}
            >
              Submit
            </Button>
          </Box>
        </Column>
      </Box>
    </Modal>
  );
};
